import { ArrowBack, ExpandCircleDown, Login } from '@mui/icons-material';
import {
  AccordionDetails,
  Box,
  CircularProgress,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  Accordion,
  AccordionSummary,
  Button,
  LocalModal,
  Switch,
} from '@/components';
import { Modals, Routes } from '@/constants';
import { lumiqColorIcon, lumiqHorizontalLogo } from '@/images';
import { AnalyticsService } from '@/services';
import {
  selectDismissedEmailPreferenceWarnings,
  selectSessionUserId,
  uiActions,
  useAppDispatch,
  useAppSelector,
  userPreferencesActions,
} from '@/store';
import {
  useFetchIterableSubscriptionsQuery,
  useUpdateIterableSubscriptionsMutation,
} from '@/store/iterable-api';
import { IterableChannelData, IterableSubscriptionGroup } from '@/types';
import { SnackbarUtils } from '@/utils';

import { EmailToggleWarning } from './EmailToggleWarning';

export const EmailPreferences = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('md'));
  const userId = useAppSelector(selectSessionUserId);
  const dispatch = useAppDispatch();

  const dismissedWarnings = useAppSelector(
    selectDismissedEmailPreferenceWarnings,
  );

  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const { token } = Object.fromEntries(query);

  const [groups, setGroups] = useState<IterableSubscriptionGroup[]>([]);
  const [pendingToggleConfirmation, setPendingToggleConfirmation] = useState<{
    channelId?: number;
    groupIndex: number;
    newIsSubscribed: boolean;
    warningMessage: IterableSubscriptionGroup['warningMessage'];
  } | null>(null);

  const { data, isLoading } = useFetchIterableSubscriptionsQuery({ token });
  const [updateSubscriptions] = useUpdateIterableSubscriptionsMutation();

  useEffect(() => {
    if (!data) return;
    const newGroups = data.map(group => {
      const anyItemsChecked = group.channels.some(
        (channel: IterableChannelData) => channel.isSubscribed,
      );
      return {
        ...group,
        isChecked: anyItemsChecked,
      };
    });
    setGroups(newGroups);
  }, [data]);

  const onToggleSwitch = ({
    channelId,
    groupIndex,
    newIsSubscribed,
    bypassWarning = false,
  }: {
    channelId?: number;
    groupIndex: number;
    newIsSubscribed: boolean;
    bypassWarning?: boolean;
  }) => {
    if (
      !bypassWarning &&
      !newIsSubscribed &&
      groups[groupIndex].warningMessage &&
      !dismissedWarnings.includes(groups[groupIndex].warningMessage.heading)
    ) {
      // Show warning modal when toggling off if group/channel has a warning message
      setPendingToggleConfirmation({
        channelId,
        groupIndex,
        newIsSubscribed,
        warningMessage: groups[groupIndex].warningMessage,
      });
      return;
    }
    const newGroups = groups.map((group, gIndex) => {
      if (gIndex === groupIndex) {
        const newChannels = group.channels.map(channel => {
          if (!channelId || channel.id === channelId) {
            AnalyticsService.toggleEmailPreference({ channel });
            return {
              ...channel,
              isSubscribed: newIsSubscribed,
            };
          }
          return channel;
        });
        return {
          ...group,
          channels: newChannels,
          // Even if at least one channel in a group becomes checked, check the group too
          isChecked: newChannels.some(channel => channel.isSubscribed),
        };
      }
      return group;
    });

    setGroups(newGroups);
  };

  const onConfirmToggle = async () => {
    if (!pendingToggleConfirmation) return;
    onToggleSwitch({
      channelId: pendingToggleConfirmation.channelId,
      groupIndex: pendingToggleConfirmation.groupIndex,
      newIsSubscribed: pendingToggleConfirmation.newIsSubscribed,
      bypassWarning: true,
    });
    dispatch(
      userPreferencesActions.dismissEmailPreferenceWarning(
        pendingToggleConfirmation.warningMessage!.heading,
      ),
    );
    setPendingToggleConfirmation(null);
    AnalyticsService.confirmEmailUnsubscribe({
      channelName: groups[pendingToggleConfirmation!.groupIndex].channels.find(
        channel => channel.id === pendingToggleConfirmation?.channelId,
      )?.name,
      groupName: groups[pendingToggleConfirmation!.groupIndex].title,
    });
  };

  const onUpdateSubscriptions = async () => {
    try {
      const channelsToUpdate = groups.flatMap(group =>
        group.channels.map(channel => ({
          id: channel.id,
          project: channel.project,
          isSubscribed: channel.isSubscribed,
        })),
      );
      await updateSubscriptions({
        channels: channelsToUpdate,
        token,
      })?.unwrap();
      SnackbarUtils.success('Your email preferences have been updated.');
      AnalyticsService.updateEmailPreferences();
    } catch {
      SnackbarUtils.error('Something went wrong, please try again later.');
    }
  };

  const onWarningModalClose = () => {
    setPendingToggleConfirmation(null);
    AnalyticsService.keepEmailSubscription({
      channelName: groups[pendingToggleConfirmation!.groupIndex].channels.find(
        channel => channel.id === pendingToggleConfirmation?.channelId,
      )?.name,
      groupName: groups[pendingToggleConfirmation!.groupIndex].title,
    });
  };

  return (
    <Box
      sx={{
        background: theme => theme.palette.gradients.pricingBackground,
        minHeight: '100vh',
        width: '100%',
      }}>
      <Box sx={{ p: { xs: 3, md: 6 } }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            mb: {
              xs: 6,
              md: 8,
            },
          }}>
          <Button
            label={isMobile ? 'Back' : 'Back To Profile'}
            variant="tertiary"
            fullWidth={false}
            skinny={false}
            startIcon={<ArrowBack />}
            onClick={() => navigate(Routes.PROFILE)}
            sx={{
              position: 'absolute',
              left: 0,
            }}
          />
          <Box
            component="img"
            alt="lumiq logo"
            sx={{ height: 42 }}
            src={isMobile ? lumiqColorIcon : lumiqHorizontalLogo}
          />
        </Box>
        <Typography
          variant="hero4"
          sx={{
            mx: 'auto',
            width: 'fit-content',
            mb: 4,
            color: 'text.main',
          }}>
          Update Email Preferences
        </Typography>
        {isLoading ? (
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              py: 10,
            }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={styles.mainBox}>
            {groups.length > 0 && (
              <Box sx={styles.group}>
                <Accordion disableGutters>
                  <AccordionSummary expandIcon={<ExpandCircleDown />}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                      }}>
                      <Typography variant="h4">Account Information</Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Essential notifications for security and access. These
                        cannot be disabled.
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        padding: 2,
                        borderRadius: 2,
                        bgcolor: 'sidebar.background',
                      }}>
                      <Typography
                        variant="body2SemiBold"
                        textTransform="uppercase">
                        Required System Notifications
                      </Typography>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { md: '1fr 1fr' },
                          gap: 1,
                          mt: 1,
                        }}>
                        <Typography variant="body2">
                          Company invitations
                        </Typography>
                        <Typography variant="body2">
                          Account recovery
                        </Typography>
                        <Typography variant="body2">
                          Payment and renewal reminders
                        </Typography>
                        <Typography variant="body2">Reset password</Typography>
                        <Typography variant="body2">
                          Email address validation
                        </Typography>
                      </Box>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Box>
            )}
            {groups.map((group, groupIndex) => (
              <Box key={groupIndex} sx={styles.group}>
                <Accordion disableGutters>
                  <AccordionSummary expandIcon={<ExpandCircleDown />}>
                    <Box
                      sx={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                        }}>
                        <Typography variant="h4">{group.title}</Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          {group.description}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Switch
                          color={'primary.main'}
                          isChecked={group.isChecked}
                          onChange={() => {
                            onToggleSwitch({
                              groupIndex: groupIndex,
                              newIsSubscribed: !group.isChecked,
                            });
                          }}
                        />
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={styles.container}>
                      {group.channels.map((channel, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}>
                          <Typography variant="body2SemiBold">
                            {channel.name}
                          </Typography>
                          <Switch
                            color={'primary.main'}
                            isChecked={channel.isSubscribed}
                            title={channel.name}
                            onChange={() => {
                              onToggleSwitch({
                                groupIndex: groupIndex,
                                channelId: channel.id,
                                newIsSubscribed: !channel.isSubscribed,
                              });
                            }}
                          />
                        </Box>
                      ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Box>
            ))}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                flexDirection: 'row-reverse',
              }}>
              {groups.length > 0 && (
                <Button
                  variant="secondary"
                  label="Update Preferences"
                  fullWidth={false}
                  onClick={onUpdateSubscriptions}
                  sx={styles.button}
                />
              )}
              {!userId && (
                <Button
                  variant="secondary"
                  label={
                    isMobile
                      ? 'Log In'
                      : `Log In to Manage ${groups.length > 0 ? 'All ' : ''}Email Preferences`
                  }
                  fullWidth={groups.length > 0 ? false : true}
                  skinny={groups.length > 0 ? true : false}
                  startIcon={<Login />}
                  onClick={() => {
                    dispatch(
                      uiActions.setActiveModal({
                        name: Modals.SIGN_UP,
                        params: {
                          defaultShowLogin: true,
                        },
                      }),
                    );
                  }}
                  sx={styles.button}
                />
              )}
            </Box>
          </Box>
        )}
      </Box>
      <LocalModal
        open={!!pendingToggleConfirmation}
        onClose={onWarningModalClose}
        name={'email unsubscribe warning'}>
        <EmailToggleWarning
          warningMessage={pendingToggleConfirmation?.warningMessage ?? null}
          onConfirm={onConfirmToggle}
          onCancel={onWarningModalClose}
        />
      </LocalModal>
    </Box>
  );
};

const styles = {
  mainBox: {
    width: '90%',
    maxWidth: 900,
    mx: 'auto',
    p: { xs: 3, md: 6 },
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    borderRadius: 4,
    boxShadow: '3px 3px 10px 0 rgba(0, 0, 0, 0.25)',
    backgroundColor: 'white',
  },
  group: {
    padding: 0.5,
    border: '1px solid',
    borderColor: 'secondary.main',
    borderRadius: 4,
  },
  container: {
    borderTop: '1px solid #00000010',
    pt: 2,
    maxWidth: '100%',
    mx: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
  },
  button: {
    mt: 1,
  },
};
